import { useId, useMemo, useState, type FocusEvent, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { axisScale } from '../lib/chartAxis'
import { monthLabels } from '../lib/months'
import type { ForecastMonth } from '../lib/forecast'

/* The one stacked-bar chart behind every balance and simulation chart on the
   Subscription and Additional Volume pages (MS-820) — the prototype's
   `volChart` (subscription-portal.html), which both products and both pages
   draw from "so the two products cannot drift apart visually": a legend with
   its title, a vertical y-axis title, round gridlines, one stacked bar per
   month (current month first, bold), a "Month" x title, an optional dashed
   reference line, and the floating `.bal-tip` card that follows the cursor
   (`wireVolTooltip`) — no longer the native `title` the older charts used. */

/** Series tones — one declaration per series for the bar segment, the legend
 * dot and the tooltip swatch (the prototype's `.bal-part/.bal-dot/
 * .bal-tip-sw` triples). */
export type SeriesTone = 'consumed' | 'contracted' | 'onDemand' | 'starting' | 'projected' | 'simGhost'

const TONE_FILL: Record<SeriesTone, string> = {
  consumed: 'bg-[var(--spent)]',
  contracted: 'bg-[var(--inv-listing)]',
  onDemand: 'bg-[var(--inv-search)]',
  starting: 'bg-[var(--inv-auth)]',
  projected: 'bg-[var(--inv-invoke)]',
  simGhost: 'bg-[var(--sim-ghost-bg)] border-[1.5px] border-dashed border-[var(--sim)] box-border',
}
// The ghost's legend dot and swatch are a touch more opaque than its bar.
const TONE_DOT: Record<SeriesTone, string> = {
  ...TONE_FILL,
  simGhost: 'bg-[var(--sim-ghost-dot)] border-[1.5px] border-dashed border-[var(--sim)] box-border',
}

export interface ChartSeries {
  key: string
  label: string
  tone: SeriesTone
}

const PLOT_HEIGHT = 216
const X_ROW = 28
const BAR_AREA = PLOT_HEIGHT - X_ROW

function sumOf(month: ForecastMonth, series: readonly ChartSeries[]): number {
  return series.reduce((total, s) => total + (month.values[s.key] ?? 0), 0)
}

export function VolumeChart({
  months,
  series,
  legendTitle,
  yTitle,
  totalLabel,
  hline,
}: Readonly<{
  months: readonly ForecastMonth[]
  series: readonly ChartSeries[]
  legendTitle: string
  yTitle: string
  /** The tooltip's total row label for a month. */
  totalLabel: (month: ForecastMonth) => string
  /** A reference level drawn across the plot (MCP's projected consumption). */
  hline?: { value: number; label: string }
}>) {
  const { t, i18n } = useTranslation()
  const [tip, setTip] = useState<{ index: number; x: number; y: number } | null>(null)
  const tipId = useId()
  const labels = useMemo(() => months.map((m) => monthLabels(m.date, i18n.language)), [months, i18n.language])

  let peak = Math.max(0, ...months.map((m) => sumOf(m, series)))
  if (hline && hline.value > peak) peak = hline.value
  if (peak <= 0) {
    return <div className="text-[12px] text-[var(--text-tertiary)]">{t('charts.nothingToProject')}</div>
  }
  const { max: axisMax, steps } = axisScale(peak)
  const gridlines = Array.from({ length: steps + 1 }, (_, i) => (axisMax / steps) * i)
  const shown = series.filter((s) => months.some((m) => (m.values[s.key] ?? 0) > 0))

  function onMove(event: MouseEvent<HTMLButtonElement>, index: number) {
    setTip({ index, x: event.clientX, y: event.clientY })
  }

  // Keyboard focus shows the same tooltip, anchored to the column's centre.
  function onFocus(event: FocusEvent<HTMLButtonElement>, index: number) {
    const rect = event.currentTarget.getBoundingClientRect()
    setTip({ index, x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 })
  }

  const tipMonth = tip === null ? null : months[tip.index]

  return (
    <div>
      <div className="flex items-center flex-wrap gap-[18px] mb-3.5 text-[12px] text-[var(--text-secondary)]">
        <span className="font-bold">{legendTitle}</span>
        {shown.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-[7px]">
            <i className={`inline-block w-[9px] h-[9px] rounded-[2px] ${TONE_DOT[s.tone]}`} />
            {s.label}
          </span>
        ))}
        {hline && (
          <span className="inline-flex items-center gap-[7px]" data-testid="chart-hline-legend">
            <i className="inline-block w-[14px] h-0 border-t-[1.5px] border-dashed border-[var(--info)]" />
            {hline.label}
          </span>
        )}
      </div>
      <div className="flex gap-2.5">
        <div
          className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary)] self-center"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          {yTitle}
        </div>
        <div className="relative flex-1 pl-14" style={{ height: PLOT_HEIGHT }}>
          {gridlines.map((v, i) => (
            <div
              key={v}
              className={`absolute left-14 right-0 ${
                i === 0
                  ? 'border-t-[1.5px] border-[var(--text-primary)] z-[4]'
                  : 'border-t border-dashed border-[var(--border-light)]'
              }`}
              style={{ bottom: X_ROW + (i / steps) * BAR_AREA }}
            >
              <span className="absolute -left-14 w-12 text-right -translate-y-1/2 text-[10px] font-semibold text-[var(--text-secondary)] tabular-nums z-[5]">
                {Math.round(v).toLocaleString(i18n.language)}
              </span>
            </div>
          ))}
          <div className="absolute left-14 top-0 bottom-7 border-l-[1.5px] border-[var(--text-primary)] z-[4]" />
          {/* Each month is a native button, so its tooltip opens on hover and
              on keyboard focus alike (Escape or leaving closes it), and a
              screen reader hears the month with the tooltip as its
              description. */}
          <div className="absolute left-14 right-0 top-0 bottom-7 flex items-end gap-1.5 pl-1.5 z-[1]">
            {months.map((m, mi) => {
              const total = sumOf(m, series)
              return (
                <button
                  type="button"
                  key={labels[mi].short}
                  data-testid="chart-col"
                  aria-label={labels[mi].label}
                  aria-describedby={tip?.index === mi ? tipId : undefined}
                  className="flex-1 min-w-0 h-full flex flex-col items-center justify-end group appearance-none bg-transparent border-0 p-0 m-0 cursor-default focus:outline-none"
                  onMouseMove={(e) => onMove(e, mi)}
                  onMouseLeave={() => setTip(null)}
                  onFocus={(e) => onFocus(e, mi)}
                  onBlur={() => setTip(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setTip(null)
                  }}
                >
                  <div
                    className="w-full max-w-[38px] flex flex-col-reverse rounded-t-[3px] overflow-hidden group-hover:outline group-hover:outline-2 group-hover:outline-[var(--accent-subtle)] group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-[var(--accent)]"
                    style={{ height: `${(total / axisMax) * 100}%` }}
                  >
                    {series.map((s) => {
                      const v = m.values[s.key] ?? 0
                      if (!v) return null
                      return (
                        <span
                          key={s.key}
                          data-series={s.key}
                          className={`block w-full ${TONE_FILL[s.tone]}`}
                          style={{ height: `${(v / total) * 100}%` }}
                        />
                      )
                    })}
                  </div>
                </button>
              )
            })}
          </div>
          {hline && (
            <div
              data-testid="chart-hline"
              className="absolute left-14 right-0 border-t-[1.5px] border-dashed border-[var(--info)] z-[3] pointer-events-none"
              style={{ bottom: X_ROW + Math.min(1, hline.value / axisMax) * BAR_AREA }}
            />
          )}
          <div className="absolute left-14 right-0 bottom-0 h-[26px] flex gap-1.5 pl-1.5">
            {labels.map((l, i) => (
              <div
                key={l.short}
                className={`flex-1 min-w-0 text-center text-[10px] whitespace-nowrap ${
                  i === 0 ? 'text-[var(--text-primary)] font-bold' : 'text-[var(--text-secondary)] font-semibold'
                }`}
              >
                {l.short}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="text-center text-[10px] uppercase tracking-wide text-[var(--text-tertiary)] mt-1.5">
        {t('charts.month')}
      </div>
      {tip !== null && tipMonth && (
        <div
          id={tipId}
          role="tooltip"
          className="fixed z-[1000] pointer-events-none bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-[0_6px_20px_rgba(0,0,0,.18)] px-2.5 py-2 text-[11.5px] text-[var(--text-primary)] min-w-[190px]"
          style={{ left: Math.min(tip.x + 14, window.innerWidth - 220), top: Math.max(8, tip.y - 110) }}
        >
          <div className="font-bold mb-1.5">{labels[tip.index].label}</div>
          {series
            .filter((s) => (tipMonth.values[s.key] ?? 0) > 0)
            .map((s) => (
              <div key={s.key} className="flex items-center gap-[7px] py-px">
                <span className={`w-2.5 h-2.5 rounded-[2px] shrink-0 ${TONE_DOT[s.tone]}`} />
                {s.label}
                <span className="ml-auto tabular-nums font-semibold">
                  {Math.round(tipMonth.values[s.key] ?? 0).toLocaleString(i18n.language)}
                </span>
              </div>
            ))}
          <div className="mt-1.5 pt-[5px] border-t border-[var(--border-light)] flex">
            {totalLabel(tipMonth)}
            <span className="ml-auto tabular-nums font-semibold">
              {Math.round(sumOf(tipMonth, series)).toLocaleString(i18n.language)}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
